<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy"
                @submit.prevent="storeCompany">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Add New Company</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length"
                             class="mb-3 bg-error"
                             closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="12">
                            <v-img :src="image" width="200"/>
                            <v-file-input v-model="formData['file']" :rules="[vRules.required]"
                                          accept="image/*" label="Select Company Logo" @change="imagePreview"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="formData['portal_id']"
                                :items="portals"
                                :loading="loading"
                                :rules="[vRules.required]"
                                item-title="name"
                                item-value="id"
                                density="compact"
                                label="Portal"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['company_name']" autofocus
                                          :rules="[vRules.required, vRules.maxLength(50)]"
                                          density="compact" label="Company Name"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['short_name']"
                                          :rules="[vRules.required, vRules.maxLength(32)]" density="compact"
                                          label="Short Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['contact_name']"
                                          :rules="[vRules.required, vRules.maxLength(50)]"
                                          density="compact"
                                          label="Contact Name"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['email']" :rules="[vRules.required, vRules.email]"
                                          density="compact"
                                          label="Email"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['phone']" :rules="[vRules.required]" density="compact"
                                          label="Phone"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_1']" density="compact" label="Address 1"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_2']" density="compact" label="Address 2"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_district']" density="compact" label="District"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_city']" density="compact" label="City"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_state']" density="compact" label="State"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_postalcode']" density="compact" label="Postal Code"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_country']" density="compact" label="Country"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['speed_threshold']" density="compact" label="Base Speed Threshold"
                                          type="number"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['speed_unit']" :items="['miles','km']" density="compact"
                                      label="Speed Unit"/>
                        </v-col>

                        <v-col cols="12" style="height: 300px">
                            <l-map ref="mapContainer"
                                   :center="[(formData.latitude??47.41322), (formData.longitude??-1.219482)]" :zoom="10"
                                   @ready="mapIsReady">
                                <l-tile-layer :url="$mapTiles.street"/>
                            </l-map>
                        </v-col>

                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid"
                           :loading="processing" color="primary" size="small" type="submit">Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import "leaflet-draw";
import L from "leaflet";
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {LMap, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {GeoSearchControl, OpenStreetMapProvider} from "leaflet-geosearch";
import {mapTiles} from "@/composables/mapConfig";

const mapContainer = ref();
const portals = ref([]);
const loading = ref(true);
const processing = ref(false);
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
});
const formData = reactive({
    valid: false,
    file: null,
    portal_id: null,
    contact_name: null,
    short_name: null,
    company_name: null,
    email: null,
    phone: null,
    addr_1: null,
    addr_2: null,
    addr_district: null,
    addr_city: null,
    addr_state: null,
    addr_postalcode: null,
    addr_country: null,
    speed_threshold: null,
    speed_unit: null,
    polygon_coordinates: null,
});

const errors = ref([]);
const image = ref(null);
const dialogSwitch = ref(props.modelValue);
const emits = defineEmits(['update:modelValue', 'addCompany']);

onMounted(() => {
    fetchPortals();
});

const fetchPortals = () => {
    send('get', `admin/portals`)
        .then((response) => {
            portals.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Portals could not be loaded', 'error')
        }).finally(() => loading.value = false)
}
const imagePreview = () => {
    formData['file'] = formData['file'];
    image.value = URL.createObjectURL(formData['file']);
}

function mapIsReady() {
    const map = mapContainer.value.leafletObject;
    map.attributionControl.remove();
    const geoSearchOptions = new GeoSearchControl({
        style: "bar",
        autoClose: true,
        keepResult: true,
        showMarker: false,
        searchLabel: "Enter address",
        provider: new OpenStreetMapProvider({searchUrl: mapTiles.searchUrl, reverseUrl: mapTiles.reverseUrl}),
    });

    map.addControl(geoSearchOptions);
    /*Add Draw Tool Bar Control*/
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    const drawControl = new L.Control.Draw({
        draw: {
            circle: false,
            polygon: {
                showArea: true,
                allowIntersection: false, // Restricts shapes to simple polygons
                drawError: {
                    color: '#9d1212', // Color the shape will turn when intersects
                    message: '<strong>Oh snap!<strong> you can\'t draw that!' // Message that will show when intersect
                }
            },
            // disable toolbar item by setting it to false
            marker: false,
            polyline: false,
            rectangle: false,
            circlemarker: false,
        },
        edit: {
            featureGroup: drawnItems
        }
    });
    map.addControl(drawControl);

    map.on('draw:created', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });
        if (e.layerType === 'polygon') {
            formData['type'] = 1;
            formData['polygon_coordinates'] = e.layer.getLatLngs()[0];
        }
        drawnItems.addLayer(e.layer);
    });

    map.on('draw:edited', function (e) {
        e.layers.eachLayer(function (layer) {
            if (layer instanceof L.Polygon) {
                formData['polygon_coordinates'] = layer.getLatLngs()[0];
            }
        });
    });

    map.on('draw:deleted', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });
        formData['polygon_coordinates'] = null;
    });
}

const storeCompany = () => {
    errors.value = [];
    processing.value = true;
    send('post', `companies`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    }).then((response) => {
        snackbar.showSnackbar(response.data.message);
        if (response.data.success) {
            emits('addCompany', response.data.data.company);
            emits('update:modelValue', false);
        }
    }).catch(function (errorResponse) {
        if (errorResponse.response.data.errors) {
            let validationErrors = errorResponse.response.data.errors;
            Object.keys(validationErrors).forEach((key) => {
                errors.value.push(validationErrors[key][0]);
            });
        } else if (errorResponse.response.data.message) {
            errors.value.push(errorResponse.response.data.message);
        }
        snackbar.showSnackbar('Something went wrong', 'error');
        document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
    }).finally(() => processing.value = false)
}
</script>
